import React, { useRef, useEffect, useState } from "react";
import "./FormInput.css";

export default function FormInput({
  type = "text",
  label,
  required = false,
  helperText,
  error,
  placeholder,
  value,
  onChange,
  options = [],
  name,
  id,
  disabled = false,
  rows = 4,
  min,
  max,
  step,
  accept,
  className = "",
  ...rest
}) {
  const inputId = id || name || label?.replace(/\s+/g, "-").toLowerCase();
  const hasError = Boolean(error);

  const [shake, setShake] = useState(false);
  const prevErrorRef = useRef(error);

  useEffect(() => {
    if (error && error !== prevErrorRef.current) {
      setShake(true);
      const t = setTimeout(() => setShake(false), 420);
      prevErrorRef.current = error;
      return () => clearTimeout(t);
    }
    prevErrorRef.current = error;
    return undefined;
  }, [error]);

  const isEmptyValue =
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0);

  const skipValidIcon = ["checkbox", "radio", "chips", "image", "textarea"];
  const isValid =
    !hasError && !disabled && !isEmptyValue && !skipValidIcon.includes(type);

  const isFileValid =
    type === "image" && !hasError && !disabled && !isEmptyValue;

  const handleChange = (e) => {
    if (!onChange) return;
    if (type === "checkbox") onChange(e.target.checked);
    else onChange(e.target.value);
  };

  const fieldClass = [
    "fi-field",
    // hasError ? "fi-field--error" : "",
    isValid ? "fi-field--valid" : "",
    disabled ? "fi-field--disabled" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const renderControl = () => {
    switch (type) {
      case "textarea":
        return (
          <textarea
            id={inputId}
            name={name}
            className={fieldClass}
            placeholder={placeholder}
            value={value}
            onChange={handleChange}
            disabled={disabled}
            rows={rows}
            aria-invalid={hasError}
            {...rest}
          />
        );

      case "select":
        return (
          <SelectDropdown
            inputId={inputId}
            name={name}
            label={label}
            value={value}
            onChange={onChange}
            options={options}
            placeholder={placeholder}
            disabled={disabled}
            hasError={hasError}
            fieldClass={fieldClass}
          />
        );

      case "radio":
        return (
          <div className="fi-option-row" role="radiogroup" aria-label={label}>
            {options.map((opt) => {
              const optValue = typeof opt === "object" ? opt.value : opt;
              const optLabel = typeof opt === "object" ? opt.label : opt;
              const selected = value === optValue;
              return (
                <label
                  className={`fi-option${selected ? " fi-option--selected" : ""}`}
                  key={optValue}
                >
                  <span
                    className={`fi-radio-dot${
                      selected ? " fi-radio-dot--selected" : ""
                    }`}
                  >
                    <span className="fi-radio-dot-inner" />
                  </span>
                  <input
                    type="radio"
                    name={name || inputId}
                    value={optValue}
                    checked={selected}
                    onChange={handleChange}
                    disabled={disabled}
                    className="fi-native-hidden"
                  />
                  <span className="fi-option-label">{optLabel}</span>
                </label>
              );
            })}
          </div>
        );

      case "checkbox":
        return (
          <label className={`fi-option${value ? " fi-option--selected" : ""}`}>
            <span
              className={`fi-checkbox${value ? " fi-checkbox--checked" : ""}`}
            >
              <svg
                className="fi-checkbox-tick"
                width="12"
                height="12"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M4 10.5L8 14.5L16 6"
                  stroke="#fff"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <input
              type="checkbox"
              id={inputId}
              name={name}
              checked={Boolean(value)}
              onChange={handleChange}
              disabled={disabled}
              aria-invalid={hasError}
              className="fi-native-hidden"
            />
            <span className="fi-option-label">{placeholder || label}</span>
          </label>
        );

      case "chips":
        return (
          <div className="fi-chip-row" role="radiogroup" aria-label={label}>
            {options.map((opt) => {
              const optValue = typeof opt === "object" ? opt.value : opt;
              const optLabel = typeof opt === "object" ? opt.label : opt;
              const selected = value === optValue;
              return (
                <button
                  key={optValue}
                  type="button"
                  className={`fi-chip${selected ? " fi-chip--selected" : ""}`}
                  onClick={() => !disabled && onChange && onChange(optValue)}
                  disabled={disabled}
                >
                  {optLabel}
                </button>
              );
            })}
          </div>
        );

      case "date":
      case "time":
        return (
          <div className="fi-input-wrap">
            <input
              id={inputId}
              name={name}
              type={type}
              className={fieldClass}
              value={value}
              onChange={handleChange}
              disabled={disabled}
              min={min}
              max={max}
              aria-invalid={hasError}
              {...rest}
            />
            {hasError && <ErrorIcon className="fi-error-icon-inline" />}
            {isValid && <ValidTick />}
          </div>
        );

      case "image":
        return (
          <ImageUpload
            inputId={inputId}
            name={name}
            accept={accept}
            value={value}
            onChange={onChange}
            disabled={disabled}
            hasError={hasError}
            isValid={isFileValid}
            rest={rest}
          />
        );

      default:
        return (
          <div className="fi-input-wrap">
            <input
              id={inputId}
              name={name}
              type={type}
              className={fieldClass}
              placeholder={placeholder}
              value={value}
              onChange={handleChange}
              disabled={disabled}
              min={min}
              max={max}
              step={step}
              aria-invalid={hasError}
              {...rest}
            />
            {hasError && <ErrorIcon className="fi-error-icon-inline" />}
            {isValid && <ValidTick />}
          </div>
        );
    }
  };

  const wrapClass = ["fi-wrap", className, shake ? "fi-shake" : ""]
    .filter(Boolean)
    .join(" ");

  if (type === "checkbox") {
    return (
      <div className={wrapClass} data-type={type}>
        {label && (
          <p className="fi-label">
            {label}
            {required && <span className="fi-required">*</span>}
          </p>
        )}
        {renderControl()}
        {hasError && (
          <p className="fi-error" key={error}>
            <ErrorIcon /> {error}
          </p>
        )}
        {!hasError && helperText && <p className="fi-helper">{helperText}</p>}
      </div>
    );
  }

  return (
    <div className={wrapClass} data-type={type}>
      {label && (
        <label htmlFor={inputId} className="fi-label">
          {label}
          {required && <span className="fi-required">*</span>}
        </label>
      )}
      {renderControl()}
      {hasError && (
        <p className="fi-error" key={error}>
          <ErrorIcon /> {error}
        </p>
      )}
      {!hasError && helperText && <p className="fi-helper">{helperText}</p>}
    </div>
  );
}

function ErrorIcon({ className }) {
  return (
    <svg
      className={className}
      width="13"
      height="13"
      viewBox="0 0 20 20"
      fill="none"
    >
      <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M10 6v5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <circle cx="10" cy="13.6" r="1" fill="currentColor" />
    </svg>
  );
}

function ValidTick() {
  return (
    <svg
      className="fi-valid-tick"
      width="16"
      height="16"
      viewBox="0 0 20 20"
      fill="none"
    >
      <circle cx="10" cy="10" r="9" fill="#16a34a" />
      <path
        d="M6 10.2l2.6 2.6L14.5 7"
        stroke="#fff"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ImageUpload({
  inputId,
  name,
  accept,
  value,
  onChange,
  disabled,
  hasError,
  isValid,
  rest,
}) {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (
      value &&
      typeof value.type === "string" &&
      value.type.startsWith("image/")
    ) {
      const url = URL.createObjectURL(value);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(null);
    return undefined;
  }, [value]);

  const commitFile = (fileList) => {
    const file = fileList?.[0];
    if (file && onChange) onChange(file);
  };

  const handleInputChange = (e) => commitFile(e.target.files);

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (disabled) return;
    commitFile(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (onChange) onChange(null);
  };

  const openPicker = () => {
    if (!disabled) fileInputRef.current?.click();
  };

  const handleBoxKeyDown = (e) => {
    if (!value && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      openPicker();
    }
  };

  const formatSize = (bytes) => {
    if (bytes === undefined || bytes === null) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const boxClass = [
    "fi-upload-box",
    hasError ? "fi-upload-box--error" : "",
    isValid ? "fi-upload-box--valid" : "",
    disabled ? "fi-upload-box--disabled" : "",
    dragActive ? "fi-upload-box--drag" : "",
    value ? "fi-upload-box--filled" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={boxClass}
      onClick={!value ? openPicker : undefined}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onKeyDown={handleBoxKeyDown}
      role="button"
      aria-disabled={disabled}
      tabIndex={disabled || value ? -1 : 0}
    >
      <input
        ref={fileInputRef}
        id={inputId}
        name={name}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        disabled={disabled}
        className="fi-upload-native-input"
        aria-invalid={hasError}
        tabIndex={-1}
        {...rest}
      />

      {!value && (
        <div className="fi-upload-placeholder">
          <span className="fi-upload-icon" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 15.5V3.5M12 3.5L7.5 8M12 3.5l4.5 4.5"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4.5 15.5v2.75A2.25 2.25 0 006.75 20.5h10.5a2.25 2.25 0 002.25-2.25V15.5"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className="fi-upload-text">
            <strong>Click to upload</strong> or drag and drop
          </span>
          {accept && (
            <span className="fi-upload-hint">
              {accept.replace(/\*/g, "").replace(/,/g, " · ")}
            </span>
          )}
        </div>
      )}

      {value && (
        <div className="fi-upload-file">
          {previewUrl ? (
            <img src={previewUrl} alt="" className="fi-upload-thumb" />
          ) : (
            <span className="fi-upload-file-icon" aria-hidden="true">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M13.5 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V7.5L13.5 3z"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinejoin="round"
                />
                <path
                  d="M13.5 3v4.5H18"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          )}
          <div className="fi-upload-file-meta">
            <span className="fi-upload-file-name">{value.name}</span>
            <span className="fi-upload-file-size">
              {formatSize(value.size)}
            </span>
          </div>
          {!disabled && (
            <button
              type="button"
              className="fi-upload-remove"
              onClick={handleRemove}
              aria-label="Remove file"
            >
              <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                <path
                  d="M5 5l10 10M15 5L5 15"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}
        </div>
      )}

      {hasError && <ErrorIcon className="fi-upload-error-icon" />}
    </div>
  );
}

function SelectDropdown({
  inputId,
  name,
  label,
  value,
  onChange,
  options,
  placeholder,
  disabled,
  hasError,
  fieldClass,
}) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const rootRef = useRef(null);

  const normalized = options.map((opt) =>
    typeof opt === "object" ? opt : { value: opt, label: opt },
  );
  const selectedOpt = normalized.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const idx = normalized.findIndex((o) => o.value === value);
    setHighlight(idx >= 0 ? idx : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const commit = (opt) => {
    if (onChange) onChange(opt.value);
    setOpen(false);
  };

  const handleTriggerKeyDown = (e) => {
    if (disabled) return;
    if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(e.key)) {
      e.preventDefault();
      setOpen(true);
    }
  };

  const handlePanelKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, normalized.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (normalized[highlight]) commit(normalized[highlight]);
    }
  };

  return (
    <div
      className="fi-select-wrap"
      ref={rootRef}
      onKeyDown={open ? handlePanelKeyDown : handleTriggerKeyDown}
    >
      <button
        type="button"
        id={inputId}
        name={name}
        className={`${fieldClass} fi-select-trigger`}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={hasError}
        onClick={() => !disabled && setOpen((o) => !o)}
      >
        <span
          className={`fi-select-value${
            !selectedOpt ? " fi-select-placeholder" : ""
          }`}
        >
          {selectedOpt ? selectedOpt.label : placeholder || "Select an option"}
        </span>
      </button>

      {hasError ? (
        <ErrorIcon className="fi-error-icon-inline" />
      ) : (
        <svg
          className={`fi-select-chevron${open ? " fi-select-chevron--open" : ""}`}
          width="14"
          height="14"
          viewBox="0 0 20 20"
          fill="none"
        >
          <path
            d="M5 7.5L10 12.5L15 7.5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}

      {open && (
        <ul
          className="fi-dropdown-panel"
          role="listbox"
          aria-label={label}
          tabIndex={-1}
        >
          {normalized.length === 0 && (
            <li className="fi-dropdown-empty">No options</li>
          )}
          {normalized.map((opt, i) => (
            <li
              key={opt.value}
              role="option"
              aria-selected={opt.value === value}
              className={`fi-dropdown-item${
                opt.value === value ? " fi-dropdown-item--selected" : ""
              }${i === highlight ? " fi-dropdown-item--highlighted" : ""}`}
              onMouseEnter={() => setHighlight(i)}
              onClick={() => commit(opt)}
            >
              {opt.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
